/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */
import dayjs from "dayjs";
import { ReactComponent as BESS } from "../../../static/images/bess.svg";
import { ReactComponent as Genset } from "../../../static/images/generator.svg";
import { ReactComponent as Solar } from "../../../static/images/solar.svg";

export const fatherAssemblies = ["Solar", "Genset", "Utility", "BESS"];
export const keys = ["Load", "Utility", "Genset", "Solar", "BESS"];
export const nodesOrder = ["Utility", "Load", "Solar", "BESS", "Genset"];
export const loadChilds = ["Solar", "BESS", "Genset"];

export const assemblyColor = {
  Utility: "#c6c6c6",
  Solar: "#a6e56a",
  Genset: "#84b953",
  BESS: "#57bcdc",
  Load: "#444444",
};
export const assemblyFillColor = {
  Utility: "#EBEBEB",
  Solar: "#ECFFDB",
  Genset: "#EFF5E9",
  BESS: "#E6F7FD",
};
export const assemblyComponent = {
  bess: BESS,
  genset: Genset,
  solar: Solar,
};

interface TSMetric {
  uuid: string;
  name: string;
  unit: string;
  table_name: string;
  value_type?: string;
}

const UNITLESS_AXIS_ID = "value";

/**
 * Dashed/undashed UUID forms compare equal (activity-graph configs still
 * store pre-migration undashed metric ids).
 */
export const uuidsMatch = (a?: string | null, b?: string | null): boolean => {
  if (a == null || b == null) return false;
  return a.replace(/-/g, "").toLowerCase() === b.replace(/-/g, "").toLowerCase();
};

/**
 * Returns a stable axis identifier for a metric. Metrics with a unit use that
 * unit string; numeric edge metrics without a unit share the "value" axis.
 */
export const getMetricAxisId = (metric: TSMetric): string => {
  if (metric.unit) return metric.unit;
  return UNITLESS_AXIS_ID;
};

export const formatDateString = (
  dateString: string,
  includeTime: boolean = true,
  timezone?: string,
): string => {
  // When `timezone` is provided, render components in that timezone (e.g. the
  // site's tz for sources like ToU that embed a non-browser offset). Without
  // it, fall back to browser-local rendering — the long-standing behavior.
  const d = timezone ? dayjs(dateString).tz(timezone) : null;

  const day = d ? d.date() : new Date(dateString).getDate();
  const month = d
    ? d.format("MMMM")
    : new Date(dateString).toLocaleString("default", { month: "long" });
  const year = d ? d.year() : new Date(dateString).getFullYear();

  const hours = d ? d.hour() : new Date(dateString).getHours();
  const minutes = d ? d.minute() : new Date(dateString).getMinutes();

  const formattedHour = hours % 12 || 12;
  const period = hours < 12 ? "am" : "pm";
  const formattedMinute = minutes.toString().padStart(2, "0");
  const formatHour = `${formattedHour}:${formattedMinute}${period}`;

  const formattedDate = `${day}${getOrdinalSuffix(day)} ${month} ${year}`;

  return includeTime ? `${formattedDate} ${formatHour}` : formattedDate;
};

export const getOrdinalSuffix = (day: number): string => {
  const suffixes = ["th", "st", "nd", "rd"];
  const remainder = day % 10;

  if (day >= 11 && day <= 13) {
    return "th";
  }

  return suffixes[remainder] || "th";
};

/**
 * Finds the time_series name associated with a given UUID. It searches
 * within the 'assembly' object's time_series array to find a matching time series.
 *
 * @param {string} uuid - The UUID of the time series to find.
 * @param {Object} site - The site object containing assemblies with time_series data.
 * @return {Object|null} The time_series object if found, or null if no matching time series is found.
 */
export const findSeries = (
  uuid: string,
  site: any,
): { tsMetric: TSMetric; assemblyName: string } | null => {
  const assembly = findAssembly(uuid, site);
  if (!assembly || !assembly.time_series) {
    return null;
  }

  const matchingTSMetric = assembly.time_series.find((ts: TSMetric) => uuidsMatch(ts.uuid, uuid));
  return matchingTSMetric !== undefined
    ? { tsMetric: matchingTSMetric, assemblyName: assembly.name }
    : null;
};

/**
 * finds the assembly that a particular time series belongs to, by the series UUID
 * @param {string} uuid - The UUID of the time series to find.
 * @param {Object} site - The site object containing assemblies with time_series data.
 * @return {Object|null} The assembly object if found, or null if no matching time series is found.
 */
export const findAssembly = (uuid: string, site: any): any | null => {
  if (!site || !site.assemblies) {
    return null;
  }

  const tsAssembly = site.assemblies.find((assembly: any) =>
    assembly?.time_series?.some((ts: TSMetric) => uuidsMatch(ts.uuid, uuid)),
  );

  return tsAssembly !== undefined ? tsAssembly : null;
};

/**
 * Formats a number with appropriate decimal places or returns a dash for null/undefined values
 * @param {number | null | undefined} num - The number to format
 * @return {number | string} The formatted number or a dash for null/undefined values
 */
export const formatNumberOrNull = (num: number | null | undefined): number | string => {
  if (num === null || num === undefined) {
    return "—";
  }
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.abs(num) >= 100 ? 0 : 2,
  }).format(num);
};

export const formatTemperature = (temperature: number | null | undefined): string => {
  if (temperature === null || temperature === undefined) {
    return "—";
  }
  return `${Math.round(temperature)}°`;
};
